<template>
  <div class="form-container estimate-form-page">
    <h1 class="visually-hidden">見積書編集</h1>

    <EstimateForm
      v-if="estimate"
      :initial-estimate="estimate"
      :is-submitting="isSubmitting"
      :error-messages="errorMessages"
      submit-label="更新"
      @submit="save"
      @cancel="backToOrigin"
    />
    <div v-else-if="isLoading" class="text-center py-5">読み込み中...</div>
    <div v-else-if="errorMessages.length" class="alert alert-danger">
      <div v-for="message in errorMessages" :key="message">{{ message }}</div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getEstimate, updateEstimate } from '@/api/axios';
import EstimateForm from '@/components/estimates/EstimateForm.vue';
import { extractApiErrors } from '@/utils/estimates';

const route = useRoute();
const router = useRouter();
const store = useStore();
const estimate = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessages = ref([]);

const queryWithoutEditOrigin = () => {
  const query = { ...route.query };
  delete query.edit_origin;
  return query;
};

const backToOrigin = () => {
  const query = queryWithoutEditOrigin();
  if (route.query.edit_origin === 'list') {
    router.push({ name: 'EstimateList', query });
    return;
  }

  router.push({
    name: 'EstimateDetail',
    params: { id: route.params.id },
    query
  });
};

const load = async () => {
  try {
    isLoading.value = true;
    errorMessages.value = [];
    const response = await getEstimate(route.params.id);
    const loaded = response.data.estimate;
    if (!loaded.permissions?.can_edit) {
      store.dispatch('setFlashMessage', {
        message: 'この見積は編集できません',
        type: 'warning'
      });
      backToOrigin();
      return;
    }
    estimate.value = loaded;
  } catch (error) {
    errorMessages.value = extractApiErrors(error);
  } finally {
    isLoading.value = false;
  }
};

const save = async payload => {
  if (isSubmitting.value) return;
  try {
    isSubmitting.value = true;
    errorMessages.value = [];
    const response = await updateEstimate(route.params.id, payload);
    const updated = response.data.estimate;
    store.dispatch('setFlashMessage', {
      message: `見積 ${updated.estimate_no} を更新しました`,
      type: 'success'
    });
    router.push({
      name: 'EstimateDetail',
      params: { id: updated.id },
      query: queryWithoutEditOrigin()
    });
  } catch (error) {
    errorMessages.value = extractApiErrors(error);
    store.dispatch('setFlashMessage', {
      message: errorMessages.value[0] || '見積の更新に失敗しました',
      type: error?.response?.status === 409 ? 'warning' : 'danger'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  document.title = '見積書編集';
  load();
});
</script>

<style scoped>
.estimate-form-page {
  box-sizing: border-box;
  width: 95%;
  max-width: 1600px;
}

@media (max-width: 767px) {
  .estimate-form-page {
    width: calc(100% - 16px);
    margin: 8px auto;
    padding: 12px;
  }
}
</style>
